import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';

const PartnerRegistrationDialog = ({ open, onOpenChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [partnerData, setPartnerData] = useState({
    registry_name: '',
    api_endpoint: '',
    contact_email: '',
    contact_phone: '',
    website_url: '',
    logo_url: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post('/public/partner/register', partnerData);
      
      if (response.data.payment_url && response.data.payment_url.startsWith('https://checkout.stripe.com')) {
        toast.success('Redirecting to payment...');
        onOpenChange(false);
        setTimeout(() => {
          window.location.href = response.data.payment_url;
        }, 1500);
      } else {
        toast.success('Registration submitted! API Key: ' + response.data.api_key);
        onOpenChange(false);
      }
      
      // Reset form
      setPartnerData({
        registry_name: '',
        api_endpoint: '',
        contact_email: '',
        contact_phone: '',
        website_url: '',
        logo_url: '',
        description: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Connect Your Microchip Registry
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-stone-600">
            Join our federated microchip search network! For just <strong>$20/month</strong>, your registry will be instantly connected and searchable.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium">Registry/Company Name *</label>
              <Input
                value={partnerData.registry_name}
                onChange={(e) => setPartnerData({...partnerData, registry_name: e.target.value})}
                placeholder="Your Company Name"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium">API Endpoint *</label>
              <Input
                value={partnerData.api_endpoint}
                onChange={(e) => setPartnerData({...partnerData, api_endpoint: e.target.value})}
                placeholder="https://api.yourregistry.com/lookup"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Email *</label>
              <Input
                type="email"
                value={partnerData.contact_email}
                onChange={(e) => setPartnerData({...partnerData, contact_email: e.target.value})}
                placeholder="contact@company.com"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone Number *</label>
              <Input
                value={partnerData.contact_phone}
                onChange={(e) => setPartnerData({...partnerData, contact_phone: e.target.value})}
                placeholder="+1-555-555-5555"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Website URL *</label>
              <Input
                value={partnerData.website_url}
                onChange={(e) => setPartnerData({...partnerData, website_url: e.target.value})}
                placeholder="https://yourregistry.com"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Logo URL *</label>
              <Input
                value={partnerData.logo_url}
                onChange={(e) => setPartnerData({...partnerData, logo_url: e.target.value})}
                placeholder="https://yourregistry.com/logo.png"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium">Description</label>
              <Input
                value={partnerData.description}
                onChange={(e) => setPartnerData({...partnerData, description: e.target.value})}
                placeholder="Brief description of your registry"
              />
            </div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg text-sm">
            <p className="font-medium text-green-800">What happens next:</p>
            <ul className="text-green-700 mt-1 space-y-1">
              <li>• Complete $20/month payment via Stripe</li>
              <li>• Your registry is instantly connected</li>
              <li>• Receive your API credentials immediately</li>
              <li>• Your logo & contact info appears in search results</li>
            </ul>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="bg-primary">
              {isLoading ? 'Processing...' : 'Continue to Payment →'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerRegistrationDialog;
